/**
 * Calendar-day helpers for coaching. A coaching "day" is a member-local
 * `YYYY-MM-DD` string in the member's own timezone, so a ritual done at 11pm
 * in Manila never lands on tomorrow's score. Pure and timezone-explicit on
 * purpose: nothing here reads the process timezone. Real-date validation is
 * `isCalendarDate` from `../accounting` (one definition for the whole package).
 */

const CALENDAR_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

function parts(day: string): [number, number, number] {
  const m = CALENDAR_DATE.exec(day);
  if (!m) throw new Error(`not a calendar date: ${day}`);
  return [Number(m[1]), Number(m[2]), Number(m[3])];
}

/** True when `timeZone` is an IANA zone this runtime can format in. */
export function isValidTimeZone(timeZone: string): boolean {
  if (!timeZone) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone });
    return true;
  } catch {
    return false;
  }
}

/** The calendar date `at` falls on in `timeZone`. Falls back to UTC on a bad zone. */
export function calendarDateIn(timeZone: string, at: Date = new Date()): string {
  const zone = isValidTimeZone(timeZone) ? timeZone : 'UTC';
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const get = (type: string) => fmt.formatToParts(at).find((p) => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/** `day` shifted by `n` calendar days (negative goes back). */
export function addCalendarDays(day: string, n: number): string {
  const [y, m, d] = parts(day);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

/** Whole days from `from` to `to` (positive when `to` is later). */
export function calendarDaysBetween(from: string, to: string): number {
  const [fy, fm, fd] = parts(from);
  const [ty, tm, td] = parts(to);
  return Math.round((Date.UTC(ty, tm - 1, td) - Date.UTC(fy, fm - 1, fd)) / 86_400_000);
}

/** Monday of the ISO week containing `day`. */
export function weekStartOf(day: string): string {
  const [y, m, d] = parts(day);
  const weekday = new Date(Date.UTC(y, m - 1, d)).getUTCDay(); // 0 = Sunday
  return addCalendarDays(day, weekday === 0 ? -6 : 1 - weekday);
}
